/**
 * Pagadito WSPG configuration.
 * Use environment variables; do not commit real UID/WSK.
 */

const trimTrailingSlashes = (value: string) => value.replace(/\/+$/, '');

/** WSPG SOAP endpoint (see Pagadito developer docs) */
const sandboxWspgDefault =
  'https://sandbox.pagadito.com/comercios/wspg/charges.php?utf8_enc';
const productionWspgDefault = 'https://comercios.pagadito.com/wspg/charges.php?utf8_enc';

/**
 * Avoid doubled `?utf8_enc&utf8_enc` (or repeated utf8_enc params) when env values are copy-pasted.
 * Keeps the first `utf8_enc` / `utf8_enc=...` and drops later duplicates.
 */
function normalizeWspgBaseUrl(value: string): string {
  const s = trimTrailingSlashes(value.trim());
  if (!s || !s.includes('?')) {
    return s;
  }
  const path = s.split('?')[0]!;
  const query = s.slice(s.indexOf('?') + 1);
  const parts = query.split('&').filter(Boolean);
  if (parts.length < 2) {
    return s;
  }
  let keptUtf8 = false;
  const out: string[] = [];
  for (const p of parts) {
    const isUtf8 = p === 'utf8_enc' || p.split('=', 1)[0] === 'utf8_enc';
    if (isUtf8) {
      if (!keptUtf8) {
        keptUtf8 = true;
        out.push(p);
      }
      continue;
    }
    out.push(p);
  }
  if (out.length === parts.length) {
    return s;
  }
  return `${path}?${out.join('&')}`;
}

function parseEnvMode(): 'sandbox' | 'production' {
  const v = (process.env.PAGADITO_ENV || 'sandbox').toLowerCase();
  return v === 'production' ? 'production' : 'sandbox';
}

const envMode = parseEnvMode();

const wspgBaseUrl = normalizeWspgBaseUrl(
  envMode === 'production'
    ? process.env.PAGADITO_WSPG_BASE_URL
      ? process.env.PAGADITO_WSPG_BASE_URL
      : productionWspgDefault
    : process.env.PAGADITO_WSPG_SANDBOX_BASE_URL
      ? process.env.PAGADITO_WSPG_SANDBOX_BASE_URL
      : sandboxWspgDefault,
);

export const pagaditoConfig = {
  uid: process.env.PAGADITO_UID || '',
  wsk: process.env.PAGADITO_WSK || '',
  currency: process.env.PAGADITO_CURRENCY || 'USD',
  feePercent: Number(process.env.PAGADITO_FEE_PERCENT || '0.05'),
  feeFixed: Number(process.env.PAGADITO_FEE_FIXED || '0.25'),
  ivaPercent: Number(process.env.PAGADITO_IVA_PERCENT || '0.13'),
  enableIva: true, 
  enableFeePercent: true,
  enableFeeFixed: true,
  enableShipping: false,
  shippingFixed: 8.00,
  environment: envMode,
  wspgBaseUrl,
  appBaseUrl: trimTrailingSlashes(
    process.env.NEXT_PUBLIC_ECOSYSTEM_APP_URL || 'http://localhost:3002',
  ),
  returnPath: '/checkout/return' as const,
  get returnUrl() {
    return `${this.appBaseUrl}${this.returnPath}`;
  },
} as const;
